/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { PureComponent } from 'react';
import { shape, func, array, bool, number, object, string } from 'prop-types';
import { connect } from 'react-redux';
import jump from 'jump.js';
import classNames from 'clsx';

import p from 'polyglot/polyglot';

import { storage } from 'utils/storage';

import { searchTalents as searchTalentsAction } from 'actions/search';
import { showSignupModal as showSignupModalAction } from 'actions/signup';

import JobCreate from 'components/JobCreate/JobCreate';
import BlackTieIcon from 'components/BlackTieIcon/BlackTieIcon';
import Spinner from 'components/Spinner/Spinner';
import TalentsFilter from 'components/TalentsFilter';
import ProfileCard from 'components/ProfileCard/ProfileCard';
import Notifications from 'components/Notifications/Notifications';
import Pagination from 'components/Pagination/Pagination';
import Button from 'components/Button/Button';
import Title from 'components/Title/Title';

import styles from './talents.scss';

/**
 * This is the Talents component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class Talents extends PureComponent {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    auth: shape({
      isAuthenticated: bool.isRequired,
      token: string.isRequired,
    }).isRequired,
    user: object.isRequired,
    bootstrap: shape({
      constants: object.isRequired,
    }).isRequired,
    talents: shape({
      loading: bool.isRequired,
      query: object.isRequired,
      pagination: object.isRequired,
      items: array.isRequired,
      filter_menu: array.isRequired,
    }).isRequired,
    searchTalents: func.isRequired,
    showSignupModal: func.isRequired,
    showHeader: bool,
    compact: bool,
    limit: number,
  };

  static defaultProps = {
    showHeader: true,
    compact: false,
    limit: 12,
  };

  /**
   * Create Talents.
   */
  constructor(props) {
    super(props);

    this.talents = React.createRef();
  }

  state = {
    createJob: false,
  };

  componentDidMount() {
    const {
      auth: { token },
      talents: { query: prevQuery },
      searchTalents,
      limit,
    } = this.props;
    // If we have a cached query, we read the query from cache.
    // Used in ManageButton to work with window.location.replace.
    const cachedQuery = storage.read('cached_talent_query');
    const { page, filters: tags, ...range } = cachedQuery || prevQuery;

    // Return the filters.
    const filters = tags ? tags.join(',') : null;

    // Clear the storage.
    storage.write('cached_talent_query', null);

    searchTalents(token, {
      latest: true,
      limit,
      filters,
      page,
      ...range,
    });
  }

  handleSubmit = data => {
    const {
      auth: { token },
      searchTalents,
      limit,
    } = this.props;

    searchTalents(token, { ...data, limit });
  };

  handlePaginationClick = page => {
    const {
      auth: { token, isAuthenticated },
      talents: { query },
      searchTalents,
    } = this.props;

    const { filters: tags } = query;
    const filters = tags.join(',');

    const searchQuery = {
      ...query,
      filters,
      page,
    };

    searchTalents(token, searchQuery);

    jump(this.talents.current, {
      offset: -80,
      duration: 260,
    });
  };

  onClickUnlock = event => {
    event.preventDefault();

    const { showSignupModal } = this.props;

    showSignupModal();
  };

  renderTalents() {
    const {
      talents: { loading, items },
    } = this.props;

    return (
      <section className={styles.items}>
        {loading && (
          <div className={styles.spinner}>
            <Spinner medium />
          </div>
        )}

        {!loading &&
          items.length > 0 &&
          items.map(talent => (
            <div className={styles.item} key={talent.talent_id}>
              <ProfileCard onClick={this.onClickUnlock} {...talent} />
            </div>
          ))}

        {!loading && items.length <= 0 && (
          <div className={styles.description}>
            <p>{p.t('talents.no_result')}</p>
          </div>
        )}
      </section>
    );
  }

  /**
   * Render Talents.
   *
   * @return {Object}
   */
  render() {
    const {
      auth: { isAuthenticated },
      user,
      talents: { query, loading, filter_menu, pagination },
      showHeader,
      compact,
    } = this.props;
    const { createJob } = this.state;

    return (
      <React.Fragment>
        <section
          className={classNames(styles.wrapper, {
            [styles.compact]: compact,
          })}
        >
          <div className={styles.container}>
            {user.is_client && user.notifications.length > 0 && (
              <div className={styles.notifications}>
                <Notifications items={user.notifications} />
              </div>
            )}

            {/* {isAuthenticated && user?.can_post_jobs && (
              <div className={styles.titleContainer}>
                <Title large title="Our talents" />
                <Button
                  className={styles.cta}
                  small
                  dark
                  ghost
                  onClick={() => this.setState({ createJob: true })}
                >
                  <BlackTieIcon
                    className={styles.icon}
                    icon="plus"
                    weight="btb"
                  />
                  CREATE PROJECT
                </Button>
              </div>
            )} */}
            <div
              className={classNames(styles.filter, {
                [styles.hasHeader]: showHeader,
              })}
            >
              <TalentsFilter
                loading={loading}
                sections={filter_menu}
                onSubmit={this.handleSubmit}
              />
            </div>
            <div className={styles.talents} ref={this.talents}>
              {showHeader && (
                <header className={styles.header}>
                  <div className={styles.headerContent}>
                    <h1>{p.t('talents.title')}</h1>
                  </div>
                </header>
              )}

              {this.renderTalents()}

              {isAuthenticated && (
                <Pagination
                  className={styles.pagination}
                  onClick={this.handlePaginationClick}
                  {...pagination}
                />
              )}
            </div>
          </div>
        </section>

        {createJob && (
          <JobCreate onClose={() => this.setState({ createJob: false })} />
        )}
      </React.Fragment>
    );
  }
}

export default connect(
  ({ auth, user, bootstrap, talents }) => ({ auth, user, bootstrap, talents }),
  {
    showSignupModal: showSignupModalAction,
    searchTalents: searchTalentsAction,
  }
)(Talents);
